@property --bezel-reveal {
  syntax: '<number>';
  inherits: true;
  initial-value: 1;
}
@property --bezel-pulse {
  syntax: '<number>';
  inherits: false;
  initial-value: 0;
}
@property --bezel-tracer {
  syntax: '<number>';
  inherits: false;
  initial-value: 0;
}
@property --bezel-stream-angle {
  syntax: '<angle>';
  /* Animated on the layer, but read by its ::after's conic mask — must inherit
     to reach the pseudo-element, or the sweep stays frozen at the initial angle. */
  inherits: true;
  initial-value: 0deg;
}
@property --bezel-primary {
  syntax: '<color>';
  inherits: true;
  initial-value: oklch(0.6 0.15 250);
}
@property --bezel-accent {
  syntax: '<color>';
  inherits: true;
  initial-value: oklch(0.7 0.15 220);
}
@property --bezel-stroke-width {
  syntax: '<length>';
  inherits: true;
  initial-value: 1px;
}
@property --bezel-corner-thickness {
  syntax: '<length>';
  inherits: true;
  initial-value: 4px;
}
@property --bezel-corner-length {
  syntax: '<length>';
  inherits: true;
  initial-value: 60px;
}
@property --bezel-bevel {
  syntax: '<length>+';
  inherits: true;
  initial-value: 12px;
}
@property --bezel-radius {
  syntax: '<length>+';
  inherits: true;
  initial-value: 0px;
}
@property --bezel-outer-glow-band {
  syntax: '<length>+';
  inherits: true;
  initial-value: 10px 0px;
}

@keyframes bezel-pulse {
  0% {
    --bezel-tracer: 0;
    --bezel-pulse: 0;
  }
  50% {
    --bezel-tracer: 0.5;
    --bezel-pulse: 1;
  }
  100% {
    --bezel-tracer: 1;
    --bezel-pulse: 0;
  }
}

@keyframes bezel-stream-rotate {
  to {
    --bezel-stream-angle: 360deg;
  }
}

@keyframes bezel-glint {
  0% {
    background-position: 100% 100%;
    opacity: 0;
  }
  10%,
  90% {
    opacity: 1;
  }
  100% {
    background-position: 0% 0%;
    opacity: 0;
  }
}

.container {
  /* Theme channel — apps rebind --bezel-primary / --bezel-accent in their
     theme; everything else derives from these two via relative color. */
  --bezel-primary: var(--mantine-color-dark-outline);
  --bezel-accent: var(--bezel-primary);

  /* Per-role source colors (overridden per-instance by a layer's `color` prop). */
  --bezel-background-color: var(--bezel-primary);
  --bezel-glow-color: var(--bezel-accent);
  --bezel-border-color: var(--bezel-primary);
  --bezel-corner-color: var(--bezel-accent);
  --bezel-outer-glow-color: var(--bezel-accent);
  --bezel-glint-color: white;

  /* Ambient liveness (0..1) → derived effect tempo/strength.
     0 == hud's old "subtle" baseline; the 0→1 ramp spans subtle→active. */
  --bezel-activity: 0;
  --bezel-pulse-duration: calc(5s - var(--bezel-activity) * 3.2s);
  --bezel-tracer-duration: calc(9s - var(--bezel-activity) * 7s);
  --bezel-glow-strength: calc(0.8 + var(--bezel-activity) * 0.2);
  --bezel-corner-floor: calc(0.55 - var(--bezel-activity) * 0.15);

  position: relative;
  transform-origin: var(--bezel-origin, 50% 0);

  .effect {
    transform-origin: var(--bezel-origin, 50% 0);
  }
}

.content {
  position: relative;
}

.effects {
  display: contents;
  .effect {
    position: absolute;
    inset: 0;
    pointer-events: none;
  }
}

.background {
  z-index: 1;
  background: radial-gradient(
    ellipse at 50% 50%,
    oklch(from var(--bezel-background-color) calc(l * 0.6) c h) 0%,
    oklch(from var(--bezel-background-color) calc(l * 0.8) c h) 100%
  );
  mix-blend-mode: plus-lighter;
  opacity: calc(var(--bezel-reveal) * var(--bezel-background-intensity, 1));
  clip-path: var(--bezel-clip-path);
  scale: calc(0.75 + var(--bezel-reveal) * 0.25);
}

.glow {
  z-index: 10;
  background: oklch(from var(--bezel-glow-color) 0.55 c h);
  mix-blend-mode: plus-lighter;
  opacity: calc((var(--bezel-glow-strength) + var(--bezel-glow-strength)) * var(--bezel-glow-intensity, 1));
  filter: blur(var(--bezel-glow-blur, 1rem));
}

.border {
  background: oklch(from var(--bezel-border-color) 0.5 calc(c * 0.85) h / var(--bezel-border-intensity, 1));
  opacity: var(--bezel-reveal);
  clip-path: var(--bezel-stroke-path);
  scale: calc(0.75 + var(--bezel-reveal) * 0.25);
}

/* A single light sweeping the frame outline. */
.tracer {
  z-index: 5;
  contain: strict;
  background-image: linear-gradient(
    178deg,
    transparent 30%,
    oklch(from var(--bezel-border-color) 0.8 calc(c * 0.25) h) 50%,
    transparent 65%
  );
  background-repeat: no-repeat;
  background-position: 50% calc(200% - 400% * var(--bezel-tracer));
  background-size: max(20rem, 200%) max(20rem, 200%);
  mix-blend-mode: plus-lighter;
  opacity: var(--bezel-reveal);
  clip-path: var(--bezel-stroke-path);
  animation: bezel-pulse var(--bezel-tracer-duration) linear infinite;
}

/* Animated streaming border — drives a rotating conic mask over the stroke. */
.stream {
  z-index: 6;
  mix-blend-mode: plus-lighter;
  filter: blur(var(--bezel-stream-blur, 5px));
  animation: bezel-stream-rotate var(--bezel-stream-speed, 24s) linear infinite;

  &::after {
    position: absolute;
    inset: 0;
    content: '';
    background: oklch(from var(--bezel-border-color) 0.4 calc(c * 0.75) h);
    clip-path: var(--bezel-outer-glow-path);
    mask: conic-gradient(from var(--bezel-stream-angle) at 50% 50%, white, #fff5, white, #fff5, white, #fff5, white);
  }
}

.stream[data-stream='thinking']::after {
  animation-duration: var(--bezel-stream-speed, 16s);
}

.stream[data-stream='speaking'] {
  animation-duration: var(--bezel-stream-speed, 8s);
}

/* Rotating conic glow around the frame — hud's idle border-animation as a standalone
   band. The conic mask sweeps via --bezel-stream-angle; filter:blur blooms the masked
   band into a soft halo that bleeds outside the frame. Stacks like any other layer. */
.outer-glow {
  z-index: 10;
  mix-blend-mode: plus-lighter;
  opacity: calc(
    var(--bezel-reveal) *
    (var(--bezel-outer-glow-floor, 0.6) + var(--bezel-pulse) * var(--bezel-outer-glow-pulse-intensity, 0.4)) *
    var(--bezel-outer-glow-intensity, 1)
  );
  filter: blur(var(--bezel-outer-glow-blur, 3px));
  animation:
    bezel-stream-rotate var(--bezel-outer-glow-speed, 25s) linear infinite,
    bezel-pulse var(--bezel-pulse-duration) linear infinite;

  &::after {
    position: absolute;
    inset: 0;
    content: '';
    background: oklch(from var(--bezel-outer-glow-color) 0.85 calc(c * 0.5) h);
    clip-path: var(--bezel-outer-glow-path);
    mask: conic-gradient(
      from var(--bezel-stream-angle) at 50% 50%,
      transparent 0%,
      white 17%,
      transparent 20%,
      transparent 40%,
      white 50%,
      white 67%,
      transparent 70%,
      transparent 80%
    );
  }
}

.corner {
  z-index: 3;
  background: oklch(from var(--bezel-corner-color) 0.78 c h);
  opacity: calc(
    (var(--bezel-corner-floor) + var(--bezel-pulse) * (1 - var(--bezel-corner-floor))) *
    var(--bezel-corner-opacity, 1) *
    var(--bezel-corner-intensity, 1)
  );
  scale: var(--bezel-reveal);
  animation: bezel-pulse var(--bezel-pulse-duration) linear infinite;
}

/* Stagger the pulse so the flare travels corner to corner. */
.corner[data-corner='tl'] {
  transform-origin: 0 0;
  animation-delay: 0s;
}
.corner[data-corner='tr'] {
  transform-origin: 100% 0;
  animation-delay: calc(var(--bezel-pulse-duration) * -0.84);
}
.corner[data-corner='br'] {
  transform-origin: 100% 100%;
  animation-delay: calc(var(--bezel-pulse-duration) * -0.625);
}
.corner[data-corner='bl'] {
  transform-origin: 0 100%;
  animation-delay: calc(var(--bezel-pulse-duration) * -0.375);
}

/* One-time entrance sweep along the frame outline. */
.glint {
  z-index: 6;
  background: linear-gradient(
    170deg,
    transparent 0%,
    transparent 30%,
    var(--bezel-glint-color) 50%,
    transparent 50%,
    transparent 100%
  );
  background-repeat: no-repeat;
  background-position: 50% 50%;
  background-size: 200% 200%;
  mix-blend-mode: plus-lighter;
  opacity: 0;
  clip-path: var(--bezel-stroke-path);
  animation: bezel-glint 1.5s ease-out 0.5s forwards;
}
